import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  ChevronRight,
  Folder,
  Plus,
  Edit2Icon,
  Check,
  X,
  Trash2Icon,
  Zap,
  Play,
  ClipboardCheck,
  FileText,
  GripVertical,
} from "lucide-react";
import { Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
  useDeleteCollection,
  useEditCollection,
  useUpdateEndpoint,
  useDeleteEndpoint,
  useReorderEndpoints,
  useGenerateTestCasesForCollection,
  useRunCollection,
  useGenerateChainedTestCases,
} from "../../../useQuery";
import Avatar from "@mui/material/Avatar";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../../../hooks";
import CollectionReviewModal from "./CollectionReviewModal";
import RequirementsModal from "./RequirementsModal";
import { useVariables } from "../../../context/VariableContext";
import GenerateTestCasesModal from "./GenerateTestCasesModal";
import Deletepopup from "./Common/Deletepopup";
import { getEndpointLabel, parseGradingIssues } from "../utils/endpointLabel";
import { COLLECTION_NAME_MAX_LENGTH } from "../../../constants/collection";

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const Collection = ({
  collection,
  nested = false,
  setUrl,
  setMethod,
  setHeaders,
  setQueryParams,
  setEndPoint,
  setActiveTab,
  setCollectionId,
  setCurrentCollectionData,
  setEndpointFormOpen,
  setResponse5,
  setChainResult,
  selectedCollectionId,
  selectedEndpointId,
  setSelectedCollectionId,
  setSelectedEndpointId,
  setBaseUrl,
  loadEndpointData,
  startGenerationStatusPolling,
  setCollectionResults,
  setChainTest,
  setLoader,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [requirementsOpen, setRequirementsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(collection?.name || "");
  const [editBaseUrl, setEditBaseUrl] = useState(collection?.baseurl || "");
  const [editingEndpointId, setEditingEndpointId] = useState(null);
  const [applyToTestCases, setApplyToTestCases] = useState(false);
  const [dialogOpen, setDialogopen] = useState(false);
  const [deleteObj, setDeleteobj] = useState({
    type: "",
    method: {},
    title: "",
  });
  const [editEndpointData, setEditEndpointData] = useState({
    method: "",
    path: "",
    name: "",
  });
  const [generateTestCasesModalOpen, setGenerateTestCasesModalOpen] = useState(false);
  const [generateChainedTestCasesModalOpen, setGenerateChainedTestCasesModalOpen] = useState(false);
  const [runCollectionModalOpen, setRunCollectionModalOpen] = useState(false);
  const [iterations, setIterations] = useState(1);
  const [orderedEndpoints, setOrderedEndpoints] = useState(collection?.endpoints || []);

  const queryClient = useQueryClient();
  const notify = useNotification();
  const { selectedEnvironmentId, GLOBAL_OPTION, NONE_OPTION } = useVariables();

  useEffect(() => {
    setOrderedEndpoints(collection?.endpoints || []);
  }, [collection?.endpoints]);

  const { mutate: handleDeleteCollection } = useDeleteCollection({
    onSuccess: () => {
      queryClient.invalidateQueries(["latestAPIResults"]);
      notify("Collection deleted successfully", "success");
    },
    onError: (error) => {
      notify(`Error deleting collection: ${error}`, "error");
    },
  });

  const { mutate: handleEditCollection, isLoading: isEditLoading } = useEditCollection({
    onSuccess: () => {
      queryClient.invalidateQueries(["latestAPIResults"]);
      notify("Collection updated successfully", "success");
      setIsEditing(false);
    },
    onError: (error) => {
      notify(`Error updating collection: ${error}`, "error");
      setEditName(collection?.name || "");
      setEditBaseUrl(collection?.baseurl || "");
    },
  });

  const { mutate: handleEditEndpoint, isLoading: isEditEndpointLoading } = useUpdateEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries(["latestAPIResults"]);
      notify("Endpoint updated successfully", "success");
      setEditingEndpointId(null);
      setEditEndpointData({ method: "", path: "" });
    },
    onError: (error) => {
      notify(`Error updating endpoint: ${error}`, "error");
    },
  });

  const { mutate: handleReorderEndpoints } = useReorderEndpoints({
    onSuccess: () => {
      queryClient.invalidateQueries(["latestAPIResults"]);
    },
    onError: (error) => {
      notify(`Error reordering endpoints: ${error}`, "error");
      setOrderedEndpoints(collection?.endpoints || []);
    },
  });

  const { mutate: handleDeleteEndpoint, isLoading: isEndpointDeleting } = useDeleteEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries(["latestAPIResults"]);
      notify("Endpoint deleted successfully", "success");
    },
    onError: (error) => {
      notify(`Error deleting endpoint: ${error}`, "error");
    },
  });

  const { mutate: handleGenerateTestCases, isLoading: isGeneratingTestCases } =
    useGenerateTestCasesForCollection({
      onSuccess: () => {
        queryClient.invalidateQueries(["latestAPIResults"]);
        notify("Test Case Generation Started", "success");
        setGenerateTestCasesModalOpen(false);
      },
      onError: (error) => {
        if (
          error.response.data ===
            "Error: Both token and company verification failed. Start from nogrunt again" ||
          error.response.data === "Error: Token verification failed. Start from nogrunt again"
        ) {
          notify("Session expired. Please log in again.", "error");
        } else {
          notify(`Error generating test cases: ${error}`, "error");
        }
        console.log(error);
      },
    });

  const { mutate: handleGenerateChainedTestCases, isLoading: isGeneratingChainedTestCases } =
    useGenerateChainedTestCases({
      onSuccess: () => {
        queryClient.invalidateQueries(["latestAPIResults"]);
        notify("Chained Test Case Generation Started", "success");
        setGenerateChainedTestCasesModalOpen(false);
      },
      onError: (error) => {
        notify(`Error generating chained test cases: ${error}`, "error");
        console.log(error);
      },
    });

  const { mutate: handleRunCollection, isLoading: isRunningCollection } = useRunCollection({
    onSuccess: (data) => {
      notify("Collection run completed successfully", "success");

      if (setLoader) {
        setLoader(false);
      }

      if (setCollectionResults) {
        setCollectionResults(data);
      }
      if (setChainTest) {
        setChainTest("collectionResults");
      }
      if (setActiveTab) {
        setActiveTab("testResults");
      }
    },
    onError: (error) => {
      notify(`Error running collection: ${error}`, "error");

      if (setLoader) {
        setLoader(false);
      }
    },
  });

  const deleteCollection = (collectionId) => {
    handleDeleteCollection({
      collectionId: collectionId,
    });
  };

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditName(collection?.name || "");
    setEditBaseUrl(collection?.baseurl || "");
  };

  const handleSaveEdit = (e) => {
    e.stopPropagation();

    if (!editName.trim()) {
      notify("Collection name is required", "error");
      return;
    }

    if (!editBaseUrl.trim()) {
      notify("Base URL is required", "error");
      return;
    }

    if (editName.trim() === collection?.name && editBaseUrl.trim() === collection?.baseurl) {
      setIsEditing(false);
      return;
    }

    handleEditCollection({
      data: {
        collectionId: collection.id,
        name: editName.trim(),
        baseurl: editBaseUrl.trim(),
      },
    });
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditName(collection?.name || "");
    setEditBaseUrl(collection?.baseurl || "");
  };

  const handleEditEndpointClick = (e, endpoint) => {
    e.stopPropagation();
    setEditingEndpointId(endpoint.id);
    setEditEndpointData({
      method: endpoint.method,
      path: endpoint.path,
      name: getEndpointLabel(endpoint),
    });
  };

  const handleSaveEndpointEdit = (e) => {
    e.stopPropagation();

    if (!editEndpointData.method.trim()) {
      notify("Method is required", "error");
      return;
    }

    if (!editEndpointData.path.trim()) {
      notify("Path is required", "error");
      return;
    }

    handleEditEndpoint({
      data: {
        endpointId: editingEndpointId,
        method: editEndpointData.method.trim(),
        path: editEndpointData.path.trim(),
        name: editEndpointData.name.trim(),
        forall: applyToTestCases,
      },
    });
  };

  const handleDeleteEndpointClick = (endpoint) => {
    if (isEndpointDeleting) return;
    handleDeleteEndpoint({ endpointId: endpoint.id });
  };

  const handleCancelEndpointEdit = (e) => {
    e.stopPropagation();
    setEditingEndpointId(null);
    setEditEndpointData({ method: "", path: "", name: "" });
    setApplyToTestCases(false);
  };

  const handleEndpointDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const reordered = Array.from(orderedEndpoints);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setOrderedEndpoints(reordered);

    handleReorderEndpoints({
      collectionId: collection.id,
      endpointIds: reordered.map((endpoint) => endpoint.id),
    });
  };

  const handleAddNewApiClick = (collection) => {
    setCollectionId(collection.id);
    setCurrentCollectionData(collection);
    setEndpointFormOpen(true);
  };

  const handleGenerateTestCasesClick = (e) => {
    e.stopPropagation();
    setGenerateTestCasesModalOpen(true);
  };

  const handleGenerateTestCasesConfirm = (data) => {
    handleGenerateTestCases(data);

    if (startGenerationStatusPolling) {
      startGenerationStatusPolling();
    }
  };

  const handleGenerateTestCasesCancel = () => {
    setGenerateTestCasesModalOpen(false);
  };

  const handleGenerateChainedTestCasesConfirm = (data) => {
    handleGenerateChainedTestCases(data);
  };

  const handleRunCollectionClick = (e) => {
    e.stopPropagation();
    if (
      !selectedEnvironmentId ||
      selectedEnvironmentId === GLOBAL_OPTION ||
      selectedEnvironmentId === NONE_OPTION
    ) {
      notify("Please select an environment before running the collection", "error");
      return;
    }

    setRunCollectionModalOpen(true);
  };

  const handleRunCollectionConfirm = () => {
    if (setChainResult) {
      setChainResult(null);
    }

    if (setLoader) {
      setLoader(true);
    }
    const endpointsData = {
      endpoints: collection?.endpoints?.map((endpoint) => endpoint.id) || [],
    };
    handleRunCollection({
      endpointsData,
      collectionId: collection.id,
      environmentId: selectedEnvironmentId,
      iterations: parseInt(iterations) || 1,
    });
    setRunCollectionModalOpen(false);
  };

  const handleRunCollectionCancel = () => {
    setRunCollectionModalOpen(false);
    setIterations(1);
  };

  const handleApiClick = (endpoint, collection) => {
    if (editingEndpointId === endpoint.id) return;

    setSelectedCollectionId(collection.id);
    setSelectedEndpointId(endpoint.id);

    setCollectionId(collection?.id);
    setEndPoint(endpoint);

    if (setBaseUrl) {
      setBaseUrl(collection.baseurl);
    }

    // Parse the endpoint path to extract query parameters
    const [pathWithoutQuery, queryString] = endpoint.path.split("?");
    const isCompleteEndpoint =
      pathWithoutQuery.includes("http") || pathWithoutQuery.startsWith("{{");
    const fullUrl = isCompleteEndpoint ? endpoint.path : `${collection.baseurl}${endpoint.path}`;

    loadEndpointData(endpoint);
    setUrl(fullUrl);

    // Parse query parameters from both the URL and endpoint.queryParams
    let queryParamsObj = {};
    // First, parse query params from URL if they exist
    if (queryString) {
      queryParamsObj = queryString.split("&").reduce((acc, param) => {
        const [key, value] = param.split("=").map(decodeURIComponent);
        if (key) acc[key] = value || "";
        return acc;
      }, {});
    }

    // Then, parse query params from endpoint.queryParams if they exist
    if (endpoint.queryParams) {
      try {
        const endpointParams =
          typeof endpoint.queryParams === "string"
            ? JSON.parse(endpoint.queryParams)
            : endpoint.queryParams;
        queryParamsObj = { ...queryParamsObj, ...endpointParams };
      } catch (error) {
        console.warn("Failed to parse endpoint.queryParams:", error);
      }
    }

    // Set query params
    setQueryParams(
      Object.keys(queryParamsObj).length > 0 ? JSON.stringify(queryParamsObj, null, 2) : "{}",
    );

    setMethod(endpoint.method);

    // Set active tab based on method
    if (endpoint.method === "GET" || endpoint.method === "DELETE") {
      setActiveTab("queryParams");
    } else {
      setActiveTab("body");
    }

    setResponse5("");
    setChainResult("");
  };

  const inputData = (inputArray) => {
    if (inputArray) {
      const outputArray = Object.entries(inputArray).map(([key, value]) => ({
        key,
        value,
      }));
      return outputArray;
    }
    return [];
  };

  const getMethodColor = (method) => {
    switch (method) {
      case "GET":
        return "text-green-700";
      case "POST":
        return "text-blue-700";
      case "PUT":
        return "text-orange-700";
      case "DELETE":
        return "text-red-700";
      default:
        return "text-gray-700";
    }
  };

  return (
    <div
      className={`font-apple flex-shrink-0 ${nested ? "mb-1.5 w-full" : "mb-3"}`}
      style={nested ? {} : { minWidth: "320px", maxWidth: "320px" }}
    >
      <div className="bg-white border border-gray-200 rounded-md shadow-sm">
        {/* Header */}
        <div
          className={`flex items-center justify-between ${nested ? "px-2 py-1.5" : "px-3 py-2"} cursor-pointer transition-all duration-200 border-b border-gray-100 ${
            selectedCollectionId === collection.id
              ? "bg-blue-200 hover:bg-blue-300"
              : "hover:bg-gray-50"
          }`}
          onClick={!isEditing ? toggleAccordion : undefined}
        >
          <div className="flex items-center flex-1 min-w-0">
            <div className="flex items-center justify-center w-[16px] mr-2">
              <ChevronRight
                size={16}
                className="text-gray-500 transition-transform duration-200"
                style={{ transform: isOpen ? "rotate(90deg)" : "none" }}
              />
            </div>
            <div className="flex items-center justify-center w-[16px] mr-2">
              <Folder size={16} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                  <div>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={COLLECTION_NAME_MAX_LENGTH}
                      className="text-sm text-gray-800 bg-white border border-gray-300 rounded px-2 py-1 w-full focus:outline-none focus:border-blue-500"
                      placeholder="Collection name"
                      disabled={isEditLoading}
                    />
                    <span className="text-xs text-gray-400 mt-1 block text-right">
                      {editName.length}/{COLLECTION_NAME_MAX_LENGTH}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={editBaseUrl}
                    onChange={(e) => setEditBaseUrl(e.target.value)}
                    className="text-xs text-gray-600 bg-white border border-gray-300 rounded px-2 py-1 w-full focus:outline-none focus:border-blue-500"
                    placeholder="Base URL"
                    disabled={isEditLoading}
                  />
                </div>
              ) : (
                <>
                  <Tooltip
                    title={collection?.name || ""}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                          wordBreak: "break-word",
                        },
                      },
                    }}
                  >
                    <span className="text-sm text-gray-800 block cursor-default truncate">
                      {collection?.name}
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 ml-1">
            {isEditing ? (
              <>
                <Tooltip title="Save changes" arrow placement="top">
                  <button
                    onClick={handleSaveEdit}
                    disabled={isEditLoading}
                    className="flex items-center justify-center p-1 rounded hover:bg-green-100 transition-all duration-200 cursor-pointer text-green-600 disabled:opacity-50"
                  >
                    <Check size={16} />
                  </button>
                </Tooltip>
                <Tooltip title="Cancel editing" arrow placement="top">
                  <button
                    onClick={handleCancelEdit}
                    disabled={isEditLoading}
                    className="flex items-center justify-center p-1 rounded hover:bg-red-100 transition-all duration-200 cursor-pointer text-red-600 disabled:opacity-50"
                  >
                    <X size={16} />
                  </button>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Edit collection" arrow placement="top">
                  <button
                    onClick={handleEditClick}
                    className="flex items-center justify-center p-1 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer text-gray-600"
                  >
                    <Edit2Icon size={16} />
                  </button>
                </Tooltip>
                <Tooltip title="Delete collection" arrow placement="top">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDialogopen(true);
                      setDeleteobj({
                        type: "Delete collection",
                        method: () => deleteCollection(collection.id),
                        title: "Delete Collection",
                      });
                    }}
                    className="text-red-400 hover:text-red-500 transition-all cursor-pointer p-1 rounded hover:bg-red-50"
                  >
                    <Trash2Icon size={16} />
                  </button>
                </Tooltip>
                <Tooltip title="Run Collection" arrow placement="top">
                  <button
                    onClick={handleRunCollectionClick}
                    disabled={isRunningCollection}
                    className="flex items-center justify-center p-1 rounded hover:bg-green-50 transition-all duration-200 cursor-pointer group disabled:opacity-50"
                  >
                    <Play size={16} className="text-green-600 group-hover:text-green-700" />
                  </button>
                </Tooltip>
                <Tooltip title="Generate Test Cases" arrow placement="top">
                  <button
                    onClick={handleGenerateTestCasesClick}
                    className="flex items-center justify-center p-1 rounded hover:bg-purple-50 transition-all duration-200 cursor-pointer group"
                  >
                    <Zap size={16} className="text-purple-600 group-hover:text-purple-700" />
                  </button>
                </Tooltip>
                <Tooltip title="Review generated test cases" arrow placement="top">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setReviewOpen(true);
                    }}
                    className="flex items-center justify-center p-1 rounded hover:bg-blue-50 transition-all duration-200 cursor-pointer group"
                  >
                    <ClipboardCheck size={16} className="text-blue-600 group-hover:text-blue-700" />
                  </button>
                </Tooltip>
                <Tooltip title="Requirements" arrow placement="top">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRequirementsOpen(true);
                    }}
                    className="flex items-center justify-center p-1 rounded hover:bg-indigo-50 transition-all duration-200 cursor-pointer group"
                  >
                    <FileText size={16} className="text-indigo-600 group-hover:text-indigo-700" />
                  </button>
                </Tooltip>
                <Tooltip title="Add new API" arrow placement="top">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddNewApiClick(collection);
                    }}
                    className="flex items-center justify-center p-1 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer"
                  >
                    <Plus size={16} className="text-gray-600" />
                  </button>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        {/* Expandable Content */}
        {isOpen && (
          <div className="bg-white">
            {/* Timing */}
            {!isEditing && (collection?.startedAt || collection?.completedAt) && (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 space-y-0.5">
                {collection?.startedAt && <div>Started {formatTimestamp(collection.startedAt)}</div>}
                {collection?.completedAt && <div>Ended {formatTimestamp(collection.completedAt)}</div>}
              </div>
            )}

            {/* Base URL Display */}
            {!isEditing && collection?.baseurl && (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs text-gray-500 font-medium">Base URL: </span>
                <span className="text-xs text-gray-600">{collection.baseurl}</span>
              </div>
            )}

            {/* Endpoints List */}
            <div className="px-4 py-3">
              <DragDropContext onDragEnd={handleEndpointDragEnd}>
                <Droppable droppableId={`collection-${collection.id}-endpoints`}>
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      className="space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-500"
                    >
                      {orderedEndpoints.map((endpoint, i) => (
                        <Draggable
                          key={endpoint.id}
                          draggableId={String(endpoint.id)}
                          index={i}
                        >
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              className="relative group"
                            >
                              {editingEndpointId === endpoint.id ? (
                      <div className="bg-gray-50 rounded border border-gray-200 p-3 space-y-2">
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editEndpointData.name}
                            onChange={(e) =>
                              setEditEndpointData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            className="w-full text-sm px-2 py-1 rounded border border-gray-300 focus:outline-none focus:border-blue-500"
                            placeholder="Endpoint name"
                            disabled={isEditEndpointLoading}
                          />
                          <div className="flex gap-2">
                            <select
                              value={editEndpointData.method}
                              onChange={(e) =>
                                setEditEndpointData((prev) => ({ ...prev, method: e.target.value }))
                              }
                              className="text-xs px-2 py-1 rounded border border-gray-300 focus:outline-none focus:border-blue-500"
                              disabled={isEditEndpointLoading}
                            >
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                              <option value="PUT">PUT</option>
                              <option value="DELETE">DELETE</option>
                            </select>
                            <input
                              type="text"
                              value={editEndpointData.path}
                              onChange={(e) =>
                                setEditEndpointData((prev) => ({ ...prev, path: e.target.value }))
                              }
                              className="flex-1 text-sm px-2 py-1 rounded border border-gray-300 focus:outline-none focus:border-blue-500"
                              placeholder="Endpoint path"
                              disabled={isEditEndpointLoading}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="applyToTestCases"
                              checked={applyToTestCases}
                              onChange={(e) => setApplyToTestCases(e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                            />
                            <label
                              htmlFor="applyToTestCases"
                              className="text-xs text-gray-600 font-apple cursor-pointer whitespace-nowrap"
                            >
                              Apply to Test Cases
                            </label>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={handleSaveEndpointEdit}
                              disabled={isEditEndpointLoading}
                              className="flex items-center justify-center p-1.5 rounded hover:bg-green-100 transition-all duration-200 cursor-pointer text-green-600 disabled:opacity-50"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={handleCancelEndpointEdit}
                              disabled={isEditEndpointLoading}
                              className="flex items-center justify-center p-1.5 rounded hover:bg-red-100 transition-all duration-200 cursor-pointer text-red-600 disabled:opacity-50"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Normal display mode for endpoint
                      <div
                        onClick={() => handleApiClick(endpoint, collection)}
                        className={`${nested ? "p-2" : "p-3"} rounded border border-gray-100 transition-all duration-200 cursor-pointer ${
                          selectedEndpointId === endpoint.id
                            ? "bg-blue-200 hover:bg-blue-300 border-blue-200"
                            : "bg-gray-50 hover:bg-[rgba(25,118,210,0.08)]"
                        }`}
                      >
                        {/* Header with name and actions */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center min-w-0 pr-2">
                            <span
                              {...draggableProvided.dragHandleProps}
                              onClick={(e) => e.stopPropagation()}
                              className="opacity-0 group-hover:opacity-100 p-0.5 -ml-1 mr-1 rounded hover:bg-gray-200 transition-all duration-200 cursor-grab active:cursor-grabbing text-gray-400 flex-shrink-0"
                              title="Drag to reorder"
                            >
                              <GripVertical size={13} />
                            </span>
                            <div className="text-xs font-semibold font-apple text-gray-700 truncate">
                              {getEndpointLabel(endpoint)}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Tooltip title="Edit endpoint" arrow placement="top">
                              <button
                                onClick={(e) => handleEditEndpointClick(e, endpoint)}
                                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer text-gray-500"
                              >
                                <Edit2Icon size={13} />
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete Endpoint" arrow placement="top">
                              <button
                                disabled={isEndpointDeleting}
                                onClick={(e) => {
                                  setDialogopen(true);
                                  e.stopPropagation();
                                  setDeleteobj({
                                    type: "Delete Endpoint",
                                    method: () => handleDeleteEndpointClick(endpoint),
                                    title: "Delete Endpoint",
                                  });
                                }}
                                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer text-gray-500 hover:text-red-500 disabled:opacity-50"
                              >
                                <Trash2Icon size={13} />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                        {/* Method and path details */}
                        <div className="flex items-center text-xs text-gray-500 gap-2">
                          <span
                            className={`px-1.5 py-0.5 rounded font-medium ${getMethodColor(endpoint.method)}`}
                            style={{
                              width: "15%",
                              backgroundColor:
                                endpoint.method === "GET"
                                  ? "#f0f9ff"
                                  : endpoint.method === "POST"
                                    ? "#eff6ff"
                                    : endpoint.method === "PUT"
                                      ? "#fff7ed"
                                      : "#fef2f2",
                              borderColor:
                                endpoint.method === "GET"
                                  ? "#bae6fd"
                                  : endpoint.method === "POST"
                                    ? "#dbeafe"
                                    : endpoint.method === "PUT"
                                      ? "#fed7aa"
                                      : "#fecaca",
                            }}
                          >
                            {endpoint.method}
                          </span>
                          <span
                            style={{ width: "75%" }}
                            title={endpoint.path}
                            className="truncate font-mono"
                          >
                            {endpoint.path}
                          </span>
                          <>
                            {endpoint.grade && (
                              <Tooltip
                                title={
                                  <>
                                    <div>Grade: {endpoint.grade}</div>
                                    {parseGradingIssues(endpoint.gradingIssues).map(
                                      (issue, issueIndex) => (
                                        <div key={issueIndex}>• {issue}</div>
                                      ),
                                    )}
                                  </>
                                }
                                arrow
                              >
                                <Avatar
                                  sx={{
                                    color:
                                      endpoint.grade === "A"
                                        ? "#23d716d3" // green
                                        : endpoint.grade === "B"
                                          ? "#AEEA00" // yellow-green
                                          : endpoint.grade === "C"
                                            ? "#FFD600" // yellow
                                            : endpoint.grade === "D"
                                              ? "#FF6D00" // orange
                                              : endpoint.grade === "E"
                                                ? "#FF3D00" // deep orange
                                                : endpoint.grade === "F"
                                                  ? "#D50000" // red
                                                  : "#000000",
                                    bgcolor: "#aaaaaa",
                                    width: 27,
                                    height: 27,
                                  }}
                                >
                                  {endpoint.grade}
                                </Avatar>
                              </Tooltip>
                            )}
                          </>
                        </div>
                      </div>
                    )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        )}
      </div>

      {/* Generate Test Cases Modal */}
      <GenerateTestCasesModal
        open={generateTestCasesModalOpen}
        onClose={handleGenerateTestCasesCancel}
        collection={collection}
        onConfirm={handleGenerateTestCasesConfirm}
        onConfirmChained={handleGenerateChainedTestCasesConfirm}
        isLoading={isGeneratingTestCases}
        isLoadingChained={isGeneratingChainedTestCases}
      />

      {/* Run Collection Modal */}
      <Dialog
        open={runCollectionModalOpen}
        onClose={handleRunCollectionCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            fontFamily: "inherit",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1.125rem" }}>Run Collection</DialogTitle>
        <DialogContent>
          <div className="text-sm text-gray-600 mb-4">
            Configure the number of iterations for running the collection{" "}
            <span className="font-medium text-gray-900">"{collection?.name}"</span>.
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Iterations
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={iterations}
              onChange={(e) => setIterations(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter number of iterations"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of times each test case will be executed (1-100)
            </p>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <button
            onClick={handleRunCollectionCancel}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer border border-gray-200 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleRunCollectionConfirm}
            disabled={isRunningCollection || !iterations || iterations < 1}
            className="px-4 py-2 text-sm bg-green-600 text-white hover:bg-green-700 transition-colors cursor-pointer rounded disabled:opacity-50"
          >
            {isRunningCollection ? "Running..." : "Run Collection"}
          </button>
        </DialogActions>
      </Dialog>
      <Deletepopup
        open={dialogOpen}
        onClose={() => setDialogopen(false)}
        onConfirm={() => {
          deleteObj.method();
          setDialogopen(false);
        }}
        itemName={deleteObj.type}
        title={deleteObj.title}
      />
      <CollectionReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        collectionId={collection?.id}
        collectionName={collection?.name}
      />
      <RequirementsModal
        open={requirementsOpen}
        onClose={() => setRequirementsOpen(false)}
        collectionId={collection?.id}
        collectionName={collection?.name}
      />
    </div>
  );
};

export default Collection;
